import { memo, useMemo, type CSSProperties } from 'react'
import { FileCode2 } from 'lucide-react'
import type { CgDesignerDesign, CgDesignerElement, CgTemplate } from '../../types/index.js'

interface Props {
  template: CgTemplate | null | undefined
}

export function templateDesign(template: CgTemplate | null | undefined): CgDesignerDesign | null {
  const design = template?.defaultData?.design
  if (!design || typeof design !== 'object' || Array.isArray(design)) return null
  const elements = (design as { elements?: unknown }).elements
  if (!Array.isArray(elements)) return null
  return design as unknown as CgDesignerDesign
}

/** Repli quand le design ne rend rien : l'image principale du modele. */
function mainImageUrl(design: CgDesignerDesign | null): string {
  const image = design?.elements.find(element => element.type === 'IMAGE' && element.imageUrl)
  return image?.imageUrl ?? ''
}

function elementText(element: CgDesignerElement): string {
  if (element.type === 'COUNTER') return `${element.counterPrefix ?? ''}00:00${element.counterSuffix ?? ''}`
  if (element.dataKey && element.text) return `${element.textPrefix ?? ''}${element.text}${element.textSuffix ?? ''}`
  return element.text ?? ''
}

interface ThumbElementProps {
  element: CgDesignerElement
  design: CgDesignerDesign
}

const ThumbElement = memo(function ThumbElement({ element, design }: ThumbElementProps) {
  // Les positions sont en pourcentage de la scene, les tailles typographiques en
  // cqw : la vignette reste fidele quelle que soit la largeur de la tuile.
  const scale = (value: number | undefined): string => `${((value ?? 0) / design.width) * 100}cqw`
  const style: CSSProperties = {
    left: `${(element.x / design.width) * 100}%`,
    top: `${(element.y / design.height) * 100}%`,
    width: `${(element.width / design.width) * 100}%`,
    height: `${(element.height / design.height) * 100}%`,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    color: element.color,
    background: element.background,
    opacity: element.opacity,
    borderRadius: scale(element.borderRadius),
    borderWidth: scale(element.borderWidth),
    borderColor: element.borderColor,
    borderStyle: element.borderWidth ? 'solid' : undefined,
    fontSize: scale(element.fontSize || 42),
    fontWeight: element.fontWeight,
    fontFamily: element.fontFamily,
    textAlign: element.textAlign,
    lineHeight: element.lineHeight,
    justifyContent: element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start',
  }

  return (
    <div className={`cg-template-thumb-element cg-template-thumb-${element.type.toLowerCase()}`} style={style}>
      {element.type === 'IMAGE' && element.imageUrl && (
        <img src={element.imageUrl} alt="" style={{ objectFit: element.imageFit || 'cover' }} />
      )}
      {/* Pas de lecture automatique : une grille de vignettes animees coute cher
          pour une information que la premiere image donne deja. */}
      {element.type === 'VIDEO' && element.videoUrl && (
        <video src={element.videoUrl} muted playsInline preload="metadata" style={{ objectFit: element.imageFit || 'contain' }} />
      )}
      {(element.type === 'TEXT' || element.type === 'COUNTER') && elementText(element)}
    </div>
  )
})

export const CgTemplateThumbnail = memo(function CgTemplateThumbnail({ template }: Props) {
  const design = useMemo(() => templateDesign(template), [template])
  const elements = useMemo(() => design?.elements.filter(element => element.visible !== false) ?? [], [design])

  if (!design || elements.length === 0) {
    const fallback = mainImageUrl(design)
    return (
      <div className="cg-template-thumb cg-transparency-grid is-empty" aria-hidden="true">
        {fallback ? <img className="cg-template-thumb-fallback" src={fallback} alt="" /> : <FileCode2 size={22} />}
      </div>
    )
  }

  // Fond opaque : la scene se suffit. Sinon le damier dit la transparence.
  const background = design.background && design.background !== 'transparent' ? design.background : undefined
  return (
    <div className={`cg-template-thumb${background ? '' : ' cg-transparency-grid'}`} style={{ background }} aria-hidden="true">
      {elements.map(element => <ThumbElement key={element.id} element={element} design={design} />)}
    </div>
  )
})
